/**
 * InstitutionProfileEditor — modal for the institution admin to edit their
 * agency profile fields: institution name, address, head-of-institution,
 * contact email. Feb 2026 · items 5-6.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Building2 } from "lucide-react";

export default function InstitutionProfileEditor({ agency, open, onClose, onSaved }) {
    const [form, setForm] = useState({
        agency_name: "",
        institution_address: "",
        head_of_institution: "",
        contact_email: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && agency) {
            setForm({
                agency_name:          agency.agency_name || "",
                institution_address:  agency.institution_address || "",
                head_of_institution:  agency.head_of_institution || "",
                contact_email:        agency.contact_email || "",
            });
        }
    }, [open, agency]);

    if (!open) return null;

    async function save(e) {
        e.preventDefault();
        setBusy(true);
        // Only send non-empty fields (backend accepts nullable Optional).
        const payload = Object.fromEntries(
            Object.entries(form).filter(([, v]) => v && String(v).trim().length),
        );
        try {
            await api.patch("/agency/profile", payload);
            toast.success("Institution profile saved");
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
             onClick={onClose} data-testid="institution-profile-modal">
            <form onSubmit={save} onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-lg bg-card border border-border rounded-lg p-5">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-primary" />
                            <div className="overline text-primary">Institution profile</div>
                        </div>
                        <h3 className="font-extrabold text-xl tracking-tight mt-1">
                            Edit your institution details
                        </h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"
                            aria-label="Close" data-testid="institution-profile-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-3">
                    <Field
                        label="Institution name"
                        value={form.agency_name}
                        onChange={(v) => setForm((f) => ({ ...f, agency_name: v }))}
                        testId="ip-name"
                        required
                    />
                    <Field
                        label="Institution address"
                        value={form.institution_address}
                        onChange={(v) => setForm((f) => ({ ...f, institution_address: v }))}
                        testId="ip-address"
                        hint="Full physical address (street, city, country)."
                        multiline
                    />
                    <Field
                        label="Head of institution"
                        value={form.head_of_institution}
                        onChange={(v) => setForm((f) => ({ ...f, head_of_institution: v }))}
                        testId="ip-head"
                        hint="Name of the current head, principal, or CEO."
                    />
                    <Field
                        label="Contact email"
                        value={form.contact_email}
                        onChange={(v) => setForm((f) => ({ ...f, contact_email: v }))}
                        testId="ip-email"
                        type="email"
                        hint="Official email for compliance and billing notices."
                    />
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose}
                            className="px-3 py-2 text-sm border border-border rounded"
                            data-testid="institution-profile-cancel">
                        Cancel
                    </button>
                    <button type="submit" disabled={busy}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-primary-foreground font-semibold rounded disabled:opacity-50"
                            data-testid="institution-profile-save">
                        <Save className="w-3.5 h-3.5" />
                        {busy ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, testId, hint, type = "text", required, multiline }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-muted-foreground mb-1">
                {label}{required && <span className="text-danger"> *</span>}
            </span>
            {multiline ? (
                <textarea
                    required={required}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                    data-testid={testId}
                />
            ) : (
                <input
                    type={type}
                    required={required}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                    data-testid={testId}
                />
            )}
            {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
        </label>
    );
}
